import type { User } from '@models/auth/user';
import { computed, ref, watch } from 'vue';
import type { Subscription } from '@models/subscription';
import { SELLER_MANAGEMENT } from '@pages/inbox/conversations-show/ProfileEnum';
import { ACCOUNT_PROFILE, SELLER_PROFILES } from '../../../layout/nav/config';

const isBuyer = ref<boolean>(true);

// eslint-disable-next-line import/prefer-default-export
export const useUserProfile = () => {
  const user = computed<User | null>(() => {
    if (!window.USER) {
      return null;
    }

    return window.USER as User;
  });

  const userProfile = computed<string>(() => {
    if (!user.value) {
      return ACCOUNT_PROFILE.MOBILE_MENU_ANONYMOUS;
    }

    const { seller_profiles: sellerProfiles, buyer } = user.value.organization;
    const hasSellerProfiles = sellerProfiles?.length > 0;

    if (!buyer) {
      return hasSellerProfiles ? ACCOUNT_PROFILE.SELLER : ACCOUNT_PROFILE.NO_PROFILE;
    }

    return hasSellerProfiles ? ACCOUNT_PROFILE.BOTH : ACCOUNT_PROFILE.BUYER;
  });

  const isSellerBoost = computed<boolean>(() => {
    if (!user.value || userProfile.value === ACCOUNT_PROFILE.BUYER) {
      return false;
    }

    const { seller_profiles: sellerProfiles } = user.value.organization;
    return Array.isArray(sellerProfiles)
        && (sellerProfiles.includes(SELLER_PROFILES.SELLER_BOOST) || sellerProfiles.includes(SELLER_PROFILES.SELLER_BOOST));
  });

  const hasNoProfile = computed<boolean>(() => userProfile.value === ACCOUNT_PROFILE.NO_PROFILE);

  const hasKYCActive = computed<boolean>(() => {
    if (!user.value?.organization.pay_organization_seller) {
      return false;
    }

    return user.value.organization.pay_organization_seller.has_kyc_active;
  });

  const hasPayBuyer = computed<boolean>(() => {
    if (!user.value?.organization) {
      return false;
    }

    const subscriptions = user.value.organization.pay_organization_buyer?.subscriptions as Subscription[] | undefined;

    // Only show buyer payment method if organization is linked to a PayOrganizationBuyer entity and has at least one subscription
    return !!subscriptions && subscriptions.length > 0;
  });

  const isProfileManagement = computed<boolean>(() => {
    if (!user.value) {
      return false;
    }

    return user.value.organization.seller_profiles.includes(SELLER_MANAGEMENT);
  });

  watch(userProfile, () => {
    if ([ ACCOUNT_PROFILE.SELLER, ACCOUNT_PROFILE.BOTH ].includes(userProfile.value)) {
      isBuyer.value = false;
      return;
    }

    isBuyer.value = [ ACCOUNT_PROFILE.BUYER, ACCOUNT_PROFILE.NO_PROFILE ].includes(userProfile.value);
  });

  const switchProfileType = (): void => {
    isBuyer.value = !isBuyer.value;
  };

  return {
    isBuyer,
    user,
    userProfile,
    isSellerBoost,
    isProfileManagement,
    switchProfileType,
    hasKYCActive,
    hasPayBuyer,
    hasNoProfile,
  };
};
